import { type CatalogModel, findModelLab, type ModelCapability } from '@oci/shared';
import { Brain, Eye, FileText, Globe2, Image, SlidersHorizontal, Wrench, Zap } from 'lucide-react';

import { type MessageKey, translate } from '~/lib/i18n';

export const CAPABILITY_ICONS: Partial<Record<ModelCapability, typeof Eye>> = {
  vision: Eye,
  reasoning: Brain,
  effort_control: SlidersHorizontal,
  tool_calling: Wrench,
  fast: Zap,
  pdf_comprehension: FileText,
  image_generation: Image,
  web_search: Globe2,
};

/**
 * The capabilities in English only: for the administration console, which is not
 * translated. Everything else asks `capabilityLabel`.
 */
export const CAPABILITY_LABELS: Record<ModelCapability, string> = {
  vision: 'Vision',
  reasoning: 'Reasoning',
  effort_control: 'Effort control',
  tool_calling: 'Tool calling',
  fast: 'Fast',
  pdf_comprehension: 'PDF comprehension',
  image_generation: 'Image generation',
  web_search: 'Web search',
};

const CAPABILITY_KEYS = {
  vision: 'chat.capability.vision',
  reasoning: 'chat.capability.reasoning',
  effort_control: 'chat.capability.effortControl',
  tool_calling: 'chat.capability.toolCalling',
  fast: 'chat.capability.fast',
  pdf_comprehension: 'chat.capability.pdfComprehension',
  image_generation: 'chat.capability.imageGeneration',
  web_search: 'chat.capability.webSearch',
} as const satisfies Record<ModelCapability, MessageKey>;

/** The same, as an item in a sentence ("Supports reasoning, tool calling"). */
const CAPABILITY_INLINE_KEYS = {
  vision: 'chat.capabilityInline.vision',
  reasoning: 'chat.capabilityInline.reasoning',
  effort_control: 'chat.capabilityInline.effortControl',
  tool_calling: 'chat.capabilityInline.toolCalling',
  fast: 'chat.capabilityInline.fast',
  pdf_comprehension: 'chat.capabilityInline.pdfComprehension',
  image_generation: 'chat.capabilityInline.imageGeneration',
  web_search: 'chat.capabilityInline.webSearch',
} as const satisfies Record<ModelCapability, MessageKey>;

/** A capability's name as a person reads it, in the language shown. */
export const capabilityLabel = (capability: ModelCapability): string =>
  translate(CAPABILITY_KEYS[capability]);

/** Capabilities joined the way the language joins a short list. */
export function capabilityList(capabilities: readonly ModelCapability[]): string {
  return capabilities
    .map((capability) => translate(CAPABILITY_INLINE_KEYS[capability]))
    .join(translate('chat.capabilityInline.separator'));
}

export const FILTER_CAPABILITIES: ModelCapability[] = [
  'fast',
  'vision',
  'reasoning',
  'effort_control',
  'tool_calling',
  'image_generation',
  'pdf_comprehension',
  'web_search',
];

export interface LabFilter {
  id: string;
  name: string;
}

export function labsFrom(models: CatalogModel[]): LabFilter[] {
  const seen = new Map<string, LabFilter>();

  for (const model of models) {
    const lab = findModelLab(model.labId);
    if (lab && !seen.has(lab.id)) seen.set(lab.id, { id: lab.id, name: lab.name });
  }
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function matchesSearch(model: CatalogModel, query: string): boolean {
  if (!query.trim()) return true;
  const needle = query.trim().toLowerCase();

  return (
    model.displayName.toLowerCase().includes(needle) ||
    model.providerLabel.toLowerCase().includes(needle) ||
    (model.description?.toLowerCase().includes(needle) ?? false) ||
    (findModelLab(model.labId)?.name.toLowerCase().includes(needle) ?? false)
  );
}

export function matchesCapabilities(
  model: CatalogModel,
  selected: ModelCapability[],
  combine: boolean,
): boolean {
  if (selected.length === 0) return true;
  return combine
    ? selected.every((capability) => model.capabilities.includes(capability))
    : selected.some((capability) => model.capabilities.includes(capability));
}

export function modelDescription(model: CatalogModel): string {
  const configured = model.description?.trim();
  if (configured) return configured;

  if (model.capabilities.length > 0)
    return translate('chat.modelPicker.supports', {
      capabilities: capabilityList(model.capabilities),
    });
  return translate('chat.modelPicker.availableThrough', { provider: model.providerLabel });
}
